import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Checkbox, Divider, Form, Header } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import ErrorIcon from '~/pages/Farmer/ClimateCheck/AnswerClimateCheck/Steps/ErrorIcon'
import { PurchasOptionsType } from '~/api/services/climaProgram'
import { DateInput } from 'semantic-ui-calendar-react'
import { useAuth } from '~/routes/components/AuthProvider'
import { LACTOSE_DRY_GROUP_KEY } from './AnimalGroupsFields'
import { formatNumber } from '~/utils/helpers'

interface Props {
  options: PurchasOptionsType
}

const PurchaseFields: React.FC<Props> = ({ options }) => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)

  const watchFeedSupplierId = useWatch({ control, name: 'feedSupplierId' })
  const watchCustomerMix = useWatch({ control, name: 'customerMix' })
  const watchPurchaseDate = useWatch({ control, name: 'purchaseDate' })

  const animalGroupOptions = useMemo(() => {
    return options?.animalGroups?.map(group => ({
      text: group.label,
      value: group.key
    })) || []
  }, [options?.animalGroups])

  const feedSupplierOptions = useMemo(() => {
    return options?.feedSuppliers?.map(supplier => ({
      text: supplier.name,
      value: supplier.id
    })) || []
  }, [options?.feedSuppliers])

  const feedProductOptions = useMemo(() => {
    if (!watchFeedSupplierId || !options?.feedSuppliers) return []
    const supplier = options.feedSuppliers.find(s => s.id === watchFeedSupplierId)
    return supplier?.products?.map(product => ({
      text: `${product.name} (${formatNumber(product.agolineContent, 2)} %)`,
      value: product.id
    })) || []
  }, [watchFeedSupplierId, options?.feedSuppliers])

  const handleKeyPress = (e: React.KeyboardEvent) => {
    e.preventDefault()
  }

  return (
    <>
      <Header as='h4'>{t('Feed Purchase')}</Header>
      <Divider />

      <Form.Group widths='equal'>
        <FormFields.Select
          name='animalGroup'
          search
          control={control}
          withoutAsterisk
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {t('Animal Group')}
              <ErrorIcon control={control} name='animalGroup' />
            </div>
          }
          onChange={(_, data) => {
            setValue('animalGroup', data.value)
            if(data.value === LACTOSE_DRY_GROUP_KEY) {
              setValue('animalGroups', [{ group: 1 }, { group: 2 }])
              return
            }
            setValue('animalGroups', [{ group: data.value }])
          }}
          options={animalGroupOptions}
          placeholder={t('Select')}
          noErrorMessage
          rules={{ required: true }}
        />

        <Controller
          control={control}
          name='purchaseDate'
          rules={{
            required: t('Please enter Purchase Date')
          }}
          render={({ fieldState }) => {
            return (
              <Form.Field error={fieldState.error}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div> {t('Purchase Date')}</div>
                  <ErrorIcon control={control} name='purchaseDate' />
                </label>
                <DateInput
                  name='purchaseDate'
                  placeholder={t('Delivered on')}
                  onKeyDown={handleKeyPress}
                  closable
                  markColor='blue'
                  localization={languageLocal?.code}
                  closeOnMouseLeave={false}
                  dateFormat='DD.MM.YYYY'
                  className='datePicker'
                  value={watchPurchaseDate || ''}
                  iconPosition='left'
                  onChange={(event, { value }) => setValue('purchaseDate', value)}
                />
              </Form.Field>
            )
          }}
        />
        <FormFields.Select
          name='feedSupplierId'
          search
          withoutAsterisk
          control={control}
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {t('Feed Supplier')}
              <ErrorIcon control={control} name='feedSupplierId' />
            </div>
          }
          options={feedSupplierOptions}
          placeholder={t('Select')}
          noErrorMessage
          rules={{ required: true }}
        />
      </Form.Group>

      <Form.Group widths='equal' style={{ alignItems: 'center' }}>
        <FormFields.Input
          control={control}
          typeInput='controled'
          allowDecimals={true}
          name={'purchasedFeedAmount'}
          decimalScale={2}
          label={t('Quantity of feed purchased according to delivery note/invoice: ')}
          placeholder={' in kg'}
          suffix=' kg'
          rules={{
            required: true
          }}
          noErrorMessage
          withoutAsterisk
        />

        <Controller
          control={control}
          name='customerMix'
          rules={{ required: true }}
          render={({ fieldState: { error } }) => (
            <Form.Field error={Boolean(error)}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {t('Customer Mix')}
                <ErrorIcon control={control} name='customerMix' />
              </label>
              <div style={{ display: 'flex', gap: 20, marginTop: '0.5em' }}>
                <Checkbox
                  radio
                  label={t('No')}
                  checked={watchCustomerMix === 0}
                  onClick={() => setValue('customerMix', 0)}
                />
                <Checkbox
                  radio
                  label={t('Yes')}
                  checked={watchCustomerMix === 1}
                  onClick={() => setValue('customerMix', 1)}
                />
              </div>
            </Form.Field>
          )}
        />
        {watchCustomerMix === 0 && <FormFields.Select
          name='feedProductId'
          search
          control={control}
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {t('Feed Product')}
              <ErrorIcon control={control} name='feedProductId' />
            </div>
            }
          withoutAsterisk
          options={feedProductOptions}
          placeholder={t('Select')}
          noErrorMessage
          disabled={!watchFeedSupplierId}
          rules={{ required: true }}
          />}
        {watchCustomerMix === 1 && <FormFields.Input
          name='agolineContent'
          control={control}
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {t('Agoline Content')}
              <ErrorIcon control={control} name='agolineContent' />
            </div>
          }
          placeholder=' %'
          withoutAsterisk
          suffix=' %'
          typeInput='controled'
          decimalScale={3}
          allowDecimals
          noErrorMessage
          rules={{
            required: true,
            min: { value: 0, message: t('Value must be at least 0') },
            max: { value: 30, message: t('Value must be at most 30') }
          }}
        />
        }
        {watchCustomerMix === undefined && <Form.Field/>}
      </Form.Group>
    </>
  )
}

export default PurchaseFields
